import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { dsDtos } from '@vectronic/openApi-admin';
import { CustomError } from '@vectronic/util';
import { Observable, map, of } from 'rxjs';

export type DeviceWithOrder = {
  order: dsDtos.Order;
  configurationIdForThisContext: string;
};

export const OOF_DB_SERVICE_PATH = new InjectionToken<string>('OOF_DB_SERVICE_PATH');

@Injectable({ providedIn: 'root' })
export class OofService {
  private httpClient = inject(HttpClient);
  oofDbServicePath = inject(OOF_DB_SERVICE_PATH);

  getOrdersByConfigNoAndOfferNumberAndFakturaId(
    configNo: number,
    offerNumber: string,
    fakturaId: string,
  ) {
    return this.httpClient.get<dsDtos.Order>(
      `${this.oofDbServicePath}/admin/orders/fakturaId/${fakturaId}/offerNumber/${offerNumber}/configNo/${configNo}`,
    );
  }

  getOrdersByOrderItemConfigurationIds(orderItemConfigurationIds: string[]) {
    const params = new HttpParams().set('configIds', orderItemConfigurationIds.join(','));
    return this.httpClient.get<dsDtos.Order[]>(
      `${this.oofDbServicePath}/admin/orders/byConfigIds`,
      { params },
    );
  }

  static findOrderByOrderItemConfigurationIdInOrderArray(
    orders: dsDtos.Order[],
    orderItemConfigurationId: string,
  ) {
    return orders.find((order) =>
      order.orderItems.find((orderItem) =>
        orderItem.orderItemConfigurations.find(({ id }) => id == orderItemConfigurationId),
      ),
    );
  }

  static findOrderItemConfigurationByIdInOrder(
    order: dsDtos.Order,
    orderItemConfigurationId: string,
  ) {
    if (!order) return undefined;
    for (const orderItem of order.orderItems) {
      const config = orderItem.orderItemConfigurations.find(
        ({ id }) => id === orderItemConfigurationId,
      );
      if (config) return config;
    }
    return undefined;
  }

  loadAndInjectOrdersToDevices<T extends dsDtos.Device>(
    devices: T[],
    buildSlot: 'buildInProduction' | 'currentBuild',
  ): Observable<(T & DeviceWithOrder)[]> {
    if (!devices.length) return of([]);
    const configIds = [
      ...new Set(
        devices.map((device) => device[buildSlot]?.orderItemConfigurationId).filter(Boolean),
      ),
    ];
    return this.getOrdersByOrderItemConfigurationIds(configIds).pipe(
      map((orders) =>
        devices.map((device) => {
          const configurationIdForThisContext = device[buildSlot]?.orderItemConfigurationId;
          const order = OofService.findOrderByOrderItemConfigurationIdInOrderArray(
            orders,
            configurationIdForThisContext,
          );
          if (!order) throw new CustomError('no order found for device ' + device.idDevice);
          return { ...device, order, configurationIdForThisContext };
        }),
      ),
    );
  }

  loadOrderNames(orderItemConfigurationIds: string[]): Observable<string[]> {
    return this.httpClient.get<string[]>(
      `${this.oofDbServicePath}/admin/orders/getNamesByConfigIds`,
      {
        params: { configIds: orderItemConfigurationIds },
      },
    );
  }
}
